import { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { SEO } from "../components/SEO";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Palette, Globe, Phone } from "lucide-react";
import { API } from "../constants";

const DesignServices = () => {
  const [formData, setFormData] = useState({
    project_type: '',
    project_title: '',
    description: '',
    style_preferences: '',
    deadline: '',
    budget_range: '',
    customer_name: '',
    customer_email: '',
    phone: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/design-request`, formData);
      toast.success('Design request submitted successfully! We\'ll contact you within 24 hours.');
      setFormData({
        project_type: '',
        project_title: '',
        description: '',
        style_preferences: '',
        deadline: '',
        budget_range: '',
        customer_name: '',
        customer_email: '',
        phone: ''
      });
    } catch (error) {
      console.error('Error submitting design request:', error);
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <SEO title="Design Services" description="Professional graphic and web design solutions from Fit Print Bros." path="/design-services" />
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Design Services</h1>
        <p className="text-xl text-gray-600">Professional graphic and web design solutions</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-12 mb-12">
        <Card className="p-8">
          <Palette className="w-12 h-12 text-purple-500 mb-4" />
          <h3 className="text-2xl font-bold mb-4">Graphic Design</h3>
          <ul className="space-y-2 text-gray-600">
            <li>• Logo Design & Branding</li>
            <li>• Marketing Materials</li>
            <li>• Social Media Graphics</li>
            <li>• Print Design for Products</li>
          </ul>
        </Card>

        <Card className="p-8">
          <Globe className="w-12 h-12 text-blue-500 mb-4" />
          <h3 className="text-2xl font-bold mb-4">Web Design</h3>
          <ul className="space-y-2 text-gray-600">
            <li>• Landing Pages</li>
            <li>• Small Business Websites</li>
            <li>• E-commerce Design</li>
            <li>• Figma to Implementation</li>
          </ul>
        </Card>
      </div>

      <Card className="p-8">
        <h2 className="text-3xl font-bold text-center mb-8">Request a Design Project</h2>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label>Project Type</Label>
              <Select value={formData.project_type} onValueChange={(value) => handleChange('project_type', value)}>
                <SelectTrigger data-testid="project-type-select" className="relative z-10">
                  <SelectValue placeholder="Select project type" />
                </SelectTrigger>
                <SelectContent className="z-50">
                  <SelectItem value="logo">Logo Design</SelectItem>
                  <SelectItem value="branding">Complete Branding</SelectItem>
                  <SelectItem value="print_design">Print Design</SelectItem>
                  <SelectItem value="web_design">Web Design</SelectItem>
                  <SelectItem value="marketing">Marketing Materials</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Budget Range</Label>
              <Select value={formData.budget_range} onValueChange={(value) => handleChange('budget_range', value)}>
                <SelectTrigger data-testid="budget-select" className="relative z-10">
                  <SelectValue placeholder="Select budget range" />
                </SelectTrigger>
                <SelectContent className="z-50">
                  <SelectItem value="under-500">Under $500</SelectItem>
                  <SelectItem value="500-1000">$500 - $1,000</SelectItem>
                  <SelectItem value="1000-2500">$1,000 - $2,500</SelectItem>
                  <SelectItem value="2500-plus">$2,500+</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Project Title</Label>
            <Input
              value={formData.project_title}
              onChange={(e) => handleChange('project_title', e.target.value)}
              placeholder="Brief title for your project"
              required
              data-testid="project-title"
            />
          </div>

          <div>
            <Label>Project Description</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Describe your project requirements, goals, and any specific details..."
              rows={4}
              required
              data-testid="project-description"
            />
          </div>

          <div>
            <Label>Style Preferences (Optional)</Label>
            <Textarea
              value={formData.style_preferences}
              onChange={(e) => handleChange('style_preferences', e.target.value)}
              placeholder="Describe your preferred style, colors, inspiration, or attach reference materials..."
              rows={3}
              data-testid="style-preferences"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label>Deadline (Optional)</Label>
              <Input
                value={formData.deadline}
                onChange={(e) => handleChange('deadline', e.target.value)}
                placeholder="When do you need this completed?"
                data-testid="deadline"
              />
            </div>
          </div>

          <div className="border-t pt-6">
            <h3 className="text-xl font-semibold mb-4">Contact Information</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label>Full Name</Label>
                <Input
                  value={formData.customer_name}
                  onChange={(e) => handleChange('customer_name', e.target.value)}
                  required
                  data-testid="customer-name-design"
                />
              </div>
              <div>
                <Label>Email Address</Label>
                <Input
                  type="email"
                  value={formData.customer_email}
                  onChange={(e) => handleChange('customer_email', e.target.value)}
                  required
                  data-testid="customer-email-design"
                />
              </div>
            </div>
            <div className="mt-4">
              <Label>Phone Number (Optional)</Label>
              <Input
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                data-testid="customer-phone"
              />
            </div>
          </div>

          <Button
            type="submit"
            size="lg"
            className="w-full bg-gradient-to-r from-purple-500 to-blue-600 hover:from-purple-600 hover:to-blue-700"
            disabled={submitting}
            data-testid="submit-design-request"
          >
            {submitting ? 'Submitting...' : 'Submit Design Request'}
          </Button>
        </form>
      </Card>
    </div>
  );
};

// Enhanced Admin Dashboard with Order Management and Payment Analytics

export { DesignServices };
export default DesignServices;
